<script>
import api from "@/api";
import DataTable from "primevue/datatable";
import Column from "primevue/column";

export default {
  components: { DataTable, Column },
  data: function() {
    return {
      isLoading: true,
      isBusy: false,
      addUserDialog: false,
      deleteUserDialog: false,
      editOrganizationDialog: false,
      userToDelete: null,
      users: [],
      email: "",
      name: "",
    };
  },
  computed: {
    validEmail() {
      return this.validateEmail(this.email);
    },
    roleOptions() {
      return [
        { label: this.$t("owner"), value: 1 },
        { label: this.$t("admin"), value: 2 },
        { label: this.$t("user"), value: 3 },
      ];
    },
    currentOrganization() {
      return this.$store.getters.currentOrganization || {};
    },
  },
  methods: {
    fetchData() {
      this.isLoading = true;
      api
        .getOrganizationUsers(this.$store.getters.currentOrganization.id)
        .then(response => {
          this.users = response.data.users;
          this.isLoading = false;
        })
        .catch(e => {
          this.isLoading = false;
        });
    },
    create() {
      api
        .createOrganizationUser(this.$store.getters.currentOrganization.id, {
          email: this.email,
          role: 2,
        })
        .then(response => {
          this.addUserDialog = false;
          this.fetchData();
          this.makeToast("info", response.data.message);
        })
        .catch(error => {
          this.addUserDialog = false;
          this.makeToast("error", error.response.data.error);
        });
    },
    cancelInvitation(id) {
      api
        .cancelInvitation(id)
        .then(response => {
          this.fetchData();
          this.makeToast("info", response.data.message);
        })
        .catch(error => {
          this.makeToast("error", error.response.data.error);
        });
    },
    removeOrganizationUser(id) {
      this.isBusy = true;
      api
        .removeOrganizationUser(id)
        .then(response => {
          if (response.data.refresh) {
            this.isBusy = false;
            this.refreshLeagueInformation("/");
            this.makeToast("info", this.$t("you-have-been-removed-from-the-organization-redirecting"));
          } else {
            this.fetchData();
            this.makeToast("info", response.data.message);
          }
        })
        .catch(error => {
          this.makeToast("error", error.response.data.error);
        });
    },
    editOrganization() {
      api
        .updateOrganization({
          id: this.currentOrganization.id || this.$store.getters.currentOrganization.id,
          name: this.name,
        })
        .then(response => {
          this.editOrganizationDialog = false;
          this.refreshLeagueInformation();
          this.makeToast("info", response.data.message);
        })
        .catch(error => {
          this.editOrganizationDialog = false;
          this.makeToast("error", error.response.data.error);
        });
    },
    openDeleteUserDialog(user) {
      this.deleteUserDialog = true;
      this.userToDelete = user;
    },
    closeDeleteUserDialog() {
      this.deleteUserDialog = false;
      this.userToDelete = null;
    },
    removeUser() {
      if (this.userToDelete) {
        this.removeOrganizationUser(this.userToDelete.id);
      }
      this.closeDeleteUserDialog();
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<template>
  <div class="p-grid">
    <UpgradeBanner v-if="currentUser && !currentUser.upgraded" />
    <div class="p-col-12 paddedCol p-mt-5 p-text-left">
      <h1 class="title is-2 p-mt-3" style="display: inline">{{ currentOrganization.name }}</h1>
      <Button
        class="p-button p-button-outlined p-button-secondary"
        style="float: right"
        :label="$t('Button.edit')"
        @click="editOrganizationDialog = true"
      />&nbsp;
    </div>

    <div class="p-col-12 paddedCol p-mt-5 p-text-left">
      <h5 class="title is-3 p-mt-3" style="display: inline">{{ $t("users") }}</h5>
      <Button
        class="p-button p-button-md"
        style="float: right"
        :label="$t('add-user')"
        @click="addUserDialog = true"
      />&nbsp;
      <Button
        style="float: right; margin-right: 10px"
        class="p-button-md p-button-outlined"
        @click="fetchData"
        :label="$t('Button.refresh')"
      />
    </div>
    <DataTable :value="users">
      <Column field="email" :header="$t('email-header')"></Column>
      <Column field="first" :header="$t('first-header')"></Column>
      <Column field="last" :header="$t('last-header')"></Column>
      <Column field="role" headerStyle="width: 8em">
        <template #header>
          {{ $t("role") }}
        </template>
        <template #body="slotProps">
          {{ roleOptions.find(option => option.value === slotProps.data.role).label }}
        </template>
      </Column>
      <Column field="status" :header="$t('status-header')"></Column>
      <Column field="id" headerStyle="width: 8em" bodyStyle="text-align: center">
        <template #header>
          {{ $t("actions") }}
        </template>
        <template #body="slotProps">
          <div v-if="slotProps.data.status === 'Active'">
            <Button
              v-show="!deleteUserDialog && slotProps.data.role != '1'"
              type="button"
              icon="pi pi-trash"
              class="p-button-danger"
              style="margin-right: .5em"
              @click="openDeleteUserDialog(slotProps.data)"
            >
            </Button>
          </div>
          <div v-else>
            <Button
              type="button"
              icon="pi pi-trash"
              class="p-button-danger"
              style="margin-right: .5em"
              @click="cancelInvitation(slotProps.data.id)"
            ></Button>
          </div>
        </template>
      </Column>
    </DataTable>

    <Dialog
      :header="$t('add-user-dialog-header')"
      :visible.sync="addUserDialog"
      :style="{ width: 'auto', maxWidth: '30vw' }"
      :modal="true"
    >
      <div class="p-grid p-my-3">
        <div class="p-col-12">
          <InputText :placeholder="$t('Account.email')" class="fs15" v-model="email" />
        </div>
        <div class="p-col-12 p-my-3">
          <Button :label="$t('Button.add')" @click="create" />
        </div>
      </div>
    </Dialog>

    <Dialog
      :header="$t('edit-org-name')"
      :visible.sync="editOrganizationDialog"
      :style="{ width: 'auto', maxWidth: '30vw' }"
      :modal="true"
    >
      <div class="p-grid p-my-3">
        <div class="p-col-12">
          <InputText :placeholder="$t('Division.name')" v-model="name" />
        </div>
        <div class="p-col-12 p-my-3">
          <Button :label="$t('Button.save')" @click="editOrganization" />
        </div>
      </div>
    </Dialog>

    <Dialog
      :header="$t('delete-user')"
      :visible.sync="deleteUserDialog"
      :style="{ width: 'auto', maxWidth: '30vw' }"
      :modal="true"
    >
      <div v-if="isBusy">
        <ProgressSpinner />
      </div>
      <div v-else-if="userToDelete">
        <div class="p-grid p-my-3">
          <div>
            <p v-html="$t('confirm-remove-user', { email: userToDelete.email })"></p>
            <p v-html="$t('cannot-be-undone')"></p>
          </div>
          <br />
          <div class="p-col-12 p-my-3">
            <Button class="p-button-sm p-button-danger" :label="$t('delete-user')" @click="removeUser" />
            <br /><br />
            <Button
              class="p-button-sm p-button-secondary"
              :label="$t('Button.cancel')"
              @click="closeDeleteUserDialog"
            />
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>

<style lang="scss">
.admin {
  td {
    text-align: left !important;
  }

  th {
    text-align: left !important;
  }
  .smallFont {
    font-size: 10px;
  }

  div {
    user-select: text !important;
  }
}
</style>
